import { useEffect, useState } from 'preact/hooks'
import { i18n } from 'i18n/i18n'

import { CurrencyValueForm } from 'models/apps'
import { SpinnerError } from 'models/calculator'
import { Calculation, CurrencyExchangeDetails, ExchangeDirection } from 'models/exchange-transfer-calculate'

import { exchangeTransfer } from 'services/exchange-transfer-calculate'
import { isHttpErrorStatus } from 'utils/promise'

export type ExchangeDataHookConfig = {
  DEFAULT_RESPONSE: Calculation
  DEFAULT_REFRESH_TIME: number
  DEFAULT_EXCHANGE_DATA: CurrencyExchangeDetails
  WITHOUT_TRANSFER_FEE?: boolean
  DOUBLE_INPUT_MODE?: boolean
}

export interface ExchangeDataHookProps {
  isSettingsLoading: boolean
  direction: ExchangeDirection
  baseCurrency: string
  counterCurrency: string
  value: CurrencyValueForm
  discountRate: number
  supportedCurrenciesPairs: string[]
  setExchangeData: (exchangeData: Calculation) => void
  setSpinnerError: (spinnerError: SpinnerError) => void
  isCurrencyPairSupported: (baseCurrency: string, counterCurrency: string) => boolean
  setIsCurrentCurrencySupported: (isCurrentCurrencySupported: boolean) => void
  isBelowMinimalAmount: boolean
  config: ExchangeDataHookConfig
}

export type BuildExchangeDetailsParams = {
  direction: ExchangeDirection
  baseCurrency: string
  counterCurrency: string
  value: CurrencyValueForm
  discountRate: number
  defaultHeaders: CurrencyExchangeDetails['headers']
  withoutTransferFee?: boolean
  isDoubleInputMode?: boolean
}

export const buildExchangeDetails = ({
  direction,
  baseCurrency,
  counterCurrency,
  value,
  discountRate,
  defaultHeaders,
  withoutTransferFee,
  isDoubleInputMode,
}: BuildExchangeDetailsParams): CurrencyExchangeDetails => {
  const isForward = direction === 'forward'

  if (isDoubleInputMode) {
    return {
      direction,
      baseAmount: isForward ? value.currentValue : '',
      counterAmount: isForward ? '' : value.currentValue,
      baseCurrency,
      counterCurrency,
      discountRate,
      headers: defaultHeaders,
      withoutTransferFee,
    }
  }

  return {
    direction: isForward ? 'back' : 'forward',
    baseAmount: isForward ? '' : value.currentValue,
    counterAmount: isForward ? value.currentValue : '',
    baseCurrency: isForward ? counterCurrency : baseCurrency,
    counterCurrency: isForward ? baseCurrency : counterCurrency,
    discountRate,
    headers: defaultHeaders,
    withoutTransferFee,
  }
}

export const useExchangeData = ({
  isSettingsLoading,
  direction,
  baseCurrency,
  counterCurrency,
  value,
  discountRate,
  supportedCurrenciesPairs,
  setExchangeData,
  setSpinnerError,
  isCurrencyPairSupported,
  setIsCurrentCurrencySupported,
  isBelowMinimalAmount,
  config,
}: ExchangeDataHookProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const { DEFAULT_RESPONSE, DEFAULT_REFRESH_TIME, DEFAULT_EXCHANGE_DATA, WITHOUT_TRANSFER_FEE, DOUBLE_INPUT_MODE } =
    config

  useEffect(() => {
    if (isSettingsLoading || supportedCurrenciesPairs.length === 0) {
      setIsLoading(false)
      return
    }

    if (!value.isValueCorrect || isBelowMinimalAmount) {
      setExchangeData(DEFAULT_RESPONSE)
      setIsLoading(false)
      return
    }

    const fetchExchangeData = async () => {
      try {
        if (
          !isCurrencyPairSupported(baseCurrency, counterCurrency) &&
          !isCurrencyPairSupported(counterCurrency, baseCurrency)
        ) {
          setIsCurrentCurrencySupported(false)
          return
        }

        setIsCurrentCurrencySupported(true)

        const exchangeDetails = buildExchangeDetails({
          direction,
          baseCurrency,
          counterCurrency,
          value,
          discountRate,
          defaultHeaders: DEFAULT_EXCHANGE_DATA.headers,
          withoutTransferFee: WITHOUT_TRANSFER_FEE,
          isDoubleInputMode: DOUBLE_INPUT_MODE,
        })

        const response = await exchangeTransfer(exchangeDetails)
        setExchangeData(response.calculation)
        setSpinnerError({ isErrorVisible: false })
      } catch (e: unknown) {
        console.error('Error fetching exchange data:', e)
        if (isHttpErrorStatus(e, 429)) {
          setSpinnerError({
            isErrorVisible: true,
            message: i18n('calculator.spinnerError.toManyRequests'),
          })
        } else {
          setSpinnerError({ isErrorVisible: true })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchExchangeData()
    const interval = setInterval(fetchExchangeData, DEFAULT_REFRESH_TIME)
    return () => clearInterval(interval)
  }, [
    isSettingsLoading,
    direction,
    baseCurrency,
    counterCurrency,
    value,
    supportedCurrenciesPairs,
    isBelowMinimalAmount,
    discountRate,
    isCurrencyPairSupported,
  ])

  return { isExchangeDataLoading: isLoading }
}
